import { Info } from '@phosphor-icons/react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import React from 'react';
import toast from 'react-hot-toast';
import { QueryClient, useQuery } from '@tanstack/react-query';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { unwrapApiResponse } from '../../../../../utils/unwrapApiResponse';

/**
 * THT-678: when a customer is Catena-connected (ELD telematics), mileage is
 * reported automatically via webhooks, so every manual submit surface is
 * disabled with this explanation. The backend enforces the same rule with a
 * 409 { reason: 'catena_connected' } on the PATCH reports endpoint.
 */
export const CATENA_MILEAGE_DISABLED_MESSAGE =
  'Mileage is synced automatically through your connected ELD, so manual entry is turned off.';

export const CATENA_CONFLICT_REASON = 'catena_connected';

/**
 * Read the customer-level flag off a mileage GET response, tolerating the
 * same envelope nestings the surrounding normalizers tolerate (bare payload,
 * axios data, or the { message, data } backend body).
 */
export const readCatenaConnected = (raw: any): boolean =>
  !!(
    raw?.catena_connected ??
    raw?.data?.catena_connected ??
    raw?.data?.data?.catena_connected
  );

/**
 * True when a mileage submit was rejected because the customer is
 * Catena-connected. Backend body is { message, data: { reason } }; both
 * nestings are checked to match the tolerant unwrapping used elsewhere.
 */
export const isCatenaConflictResponse = (
  response: AxiosResponse | null
): boolean => {
  if (response?.status !== HttpStatusCode.Conflict) return false;
  const reason =
    response?.data?.data?.reason ?? (response?.data as any)?.reason;
  return reason === CATENA_CONFLICT_REASON;
};

/**
 * Drop the cached Catena state so the submit surfaces refetch it. Called when
 * connection state changes (connect/disconnect in useCatenaConnection) and
 * after a submit 409, so the disabled state tracks reality instead of the
 * 15-minute staleTime.
 */
export const invalidateCatenaMileageQueries = (
  queryClient: QueryClient
): void => {
  queryClient.invalidateQueries({ queryKey: ['mileageReportRequests'] });
  queryClient.invalidateQueries({ queryKey: ['catenaConnected'] });
};

/**
 * Shared 409 handling for the three mileage submit surfaces: surfaces the
 * backend guard as a toast (AxiosWrapper's toast switch does not cover 409)
 * and refetches so a stale client picks up the disabled state. Returns true
 * when the response was a conflict it handled.
 */
export const handleMileageConflictResponse = (
  response: AxiosResponse | null,
  queryClient: QueryClient
): boolean => {
  if (response?.status !== HttpStatusCode.Conflict) return false;
  toast.error(
    isCatenaConflictResponse(response)
      ? CATENA_MILEAGE_DISABLED_MESSAGE
      : 'Mileage could not be saved due to a conflict. Please refresh and try again.'
  );
  invalidateCatenaMileageQueries(queryClient);
  return true;
};

/** Info banner shown on the submit surfaces when manual entry is disabled. */
export const CatenaMileageNotice = () => (
  <div
    role="status"
    className="flex w-full flex-row items-center gap-2 rounded-[8px] border-[1px] border-blue-200 bg-blue-50 px-4 py-3"
  >
    <Info size={20} weight="fill" className="shrink-0 text-blue-500" />
    <p className="text-[14px] leading-[20px] text-blue-800">
      {CATENA_MILEAGE_DISABLED_MESSAGE}
    </p>
  </div>
);

/**
 * Customer-level Catena flag for the per-asset SubmitMileageUnitModal, resolved
 * once in the modal's parent (AssetDetails) and drilled down as a prop so the
 * modal has no query of its own — one source of truth, no redundant fetch on
 * open. Reuses the reports endpoint that carries catena_connected (no dedicated
 * Catena polling); pass `enabled` so it only fires for assets that actually
 * expose the Submit Mileage entry point. A failed probe throws
 * (unwrapApiResponse) so React Query retries instead of caching a stale value.
 *
 * Returns a tri-state:
 *   - `undefined` while the first fetch is in flight — callers treat "not yet
 *     known" as disabled so a connected customer never sees an editable flash.
 *   - `true` / `false` once resolved. On error it resolves `false` (fail open;
 *     the backend 409 guard is the hard stop for a connected customer).
 */
export const useCatenaConnected = (enabled = true): boolean | undefined => {
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();

  const { data, isLoading } = useQuery({
    queryKey: ['catenaConnected', userId],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}?page=1&pageSize=1`,
        'get',
        {}
      );
      const root = unwrapApiResponse<any>(
        response,
        'Could not load your ELD connection status.'
      );
      return readCatenaConnected(root);
    },
    enabled: enabled && !!userId && !!userRecord?.userType,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  return isLoading ? undefined : (data ?? false);
};
